import { Stack } from '@/components/layout/stack';
import { StackLayoutProps } from '@/components/layout/stack/types';
import { useScaling } from '@/hooks';
import { createContext, memo, useContext, useEffect, useEffectEvent, useState } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export type AppHeaderRootProps = StackLayoutProps;

export type AppHeaderLeftProps = {
  children: React.ReactNode;
};

export type AppHeaderRightProps = {
  children: React.ReactNode;
};

export type AppHeaderTitleProps = {
  children: React.ReactNode;
};

type AppHeaderContextType = {
  hasLeft?: boolean;
  hasRight?: boolean;
  setHasLeft: (hasLeft: boolean) => void;
  setHasRight: (hasRight: boolean) => void;
};

const AppHeaderContext = createContext<AppHeaderContextType>({
  hasLeft: false,
  hasRight: false,
  setHasLeft: () => {},
  setHasRight: () => {},
});

const AppHeaderRoot = ({ children, ...props }: AppHeaderRootProps) => {
  const [hasLeft, setHasLeft] = useState<boolean>(false);
  const [hasRight, setHasRight] = useState<boolean>(false);
  const insets = useSafeAreaInsets();
  const { scale } = useScaling();
  const paddingTop = insets.top + scale(4);

  return (
    <AppHeaderContext.Provider value={{ hasLeft, hasRight, setHasLeft, setHasRight }}>
      <Stack
        direction="row"
        paddingTop={paddingTop}
        zIndex={9999}
        paddingX={scale(16)}
        height={paddingTop + scale({ phone: 46, tablet: 70 })}
        justify="space-between"
        align="center"
        {...props}>
        {!hasLeft && <Stack width={40} />}
        <Stack flex={1} direction="row">
          {children}
        </Stack>
        {!hasRight && <Stack width={40} />}
      </Stack>
    </AppHeaderContext.Provider>
  );
};

const AppHeaderLeft = ({ children }: AppHeaderLeftProps) => {
  const { setHasLeft } = useContext(AppHeaderContext);
  const { scale } = useScaling();
  const effectEvent = useEffectEvent(() => {
    setHasLeft(true);
  });
  useEffect(() => {
    effectEvent();
  }, []);
  return (
    <Stack minWidth={scale({ phone: 70, tablet: 160 })} direction="row" align="center">
      {children}
    </Stack>
  );
};

const AppHeaderRight = ({ children }: AppHeaderRightProps) => {
  const { setHasRight } = useContext(AppHeaderContext);
  const { scale } = useScaling();
  const effectEvent = useEffectEvent(() => {
    setHasRight(true);
  });
  useEffect(() => {
    effectEvent();
  }, []);
  return (
    <Stack
      minWidth={scale({ phone: 70, tablet: 160 })}
      justify="flex-end"
      direction="row"
      align="center">
      {children}
    </Stack>
  );
};

const AppHeaderTitle = ({ children }: AppHeaderTitleProps) => {
  return (
    <Stack flex={1} align="center" justify="center" direction="row">
      {children}
    </Stack>
  );
};

export const AppHeader = Object.assign(memo(AppHeaderRoot), {
  Left: memo(AppHeaderLeft),
  Right: memo(AppHeaderRight),
  Title: memo(AppHeaderTitle),
});
